import React, { useCallback, useEffect, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { Dialog, Label, ShelfHeader, Button, TextField, WarningMessage } from '../../components/widgets';
import PasswordStrengthMeter from '../../auth/components/PasswordStrengthMeter';
import useZxcvbn from '../../auth/useZxcvbn';
import User from '../../services/user';
import { SHOW_CHANGE_PASSWORD_MODAL_EVENT } from '../consts';
import css from './ChangePasswordDialog.css';

const userApi = new User();

const MIN_STRENGTH_SCORE = 2;

const INITIAL_STATE = {
    oldPassword: '',
    newPassword: '',
    saving: false,
    success: false,
    error: '',
};

function ChangePasswordDialog() {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(INITIAL_STATE);
    const { oldPassword, newPassword, saving, success, error } = state;
    const { failed, score } = useZxcvbn();

    const close = useCallback(() => {
        setOpen(false);
        setState(INITIAL_STATE);
    }, []);

    useEffect(() => {
        const onEvent = () => {
            setState(INITIAL_STATE);
            setOpen(true);
        };
        window.addEventListener(SHOW_CHANGE_PASSWORD_MODAL_EVENT, onEvent);
        return () => window.removeEventListener(SHOW_CHANGE_PASSWORD_MODAL_EVENT, onEvent);
    }, []);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onNavigate = () => close();
        window.addEventListener('hashchange', onNavigate);
        window.addEventListener('popstate', onNavigate);
        return () => {
            window.removeEventListener('hashchange', onNavigate);
            window.removeEventListener('popstate', onNavigate);
        };
    }, [open, close]);

    const onSave = useCallback(() => {
        setState(prev => ({ ...prev, saving: true, success: false, error: '' }));
        userApi
            .savePassword({ oldPassword, newPassword })
            .then(response => {
                if (response.status !== 0) {
                    setState(prev => ({ ...prev, saving: false, error: response.value || 'internal error' }));
                } else {
                    setState(prev => ({ ...prev, saving: false, success: true }));
                }
            })
            .catch(() => {
                setState(prev => ({ ...prev, saving: false, error: 'internal error' }));
            });
    }, [oldPassword, newPassword]);

    const currentScore = score(newPassword);
    const strongEnough = failed ? !!newPassword : currentScore !== null && currentScore > MIN_STRENGTH_SCORE;
    const onPrimaryClick = success ? close : onSave;
    const primaryDisabled = success ? false : saving || !strongEnough;

    return (
        <Dialog open={open} onEscapePress={close} noPadding className={css.dialog}>
            <ShelfHeader text="STATIC.TOPBAR_HEADERS.CHANGE_PASSWORD" onClose={close} />
            <>
                <WarningMessage show={!!error} message={error} type="error" showIcon={false} />
                <WarningMessage
                    show={success}
                    message="Password changed successfully"
                    type="success"
                    showIcon={false}
                />
                <div className={css.dialogBody}>
                    <Label type="fieldLabel" text="STATIC.PLACEHOLDERS.CURRENT_PASSWORD" />
                    <TextField
                        password
                        value={oldPassword}
                        onChange={value => setState(prev => ({ ...prev, oldPassword: value }))}
                    />
                    <Label type="fieldLabel" text="STATIC.PLACEHOLDERS.NEW_PASSWORD" />
                    <TextField
                        password
                        value={newPassword}
                        onChange={value => setState(prev => ({ ...prev, newPassword: value }))}
                    />
                    {failed ? (
                        <div className={css.strengthUnavailable}>
                            <Translate id="STATIC.PAGES.LOGIN.STRENGTH_CHECK_UNAVAILABLE" />
                        </div>
                    ) : (
                        <PasswordStrengthMeter score={newPassword ? currentScore : null} />
                    )}
                </div>
                <div className={css.dialogFooter}>
                    <Button
                        type="flat"
                        text="STATIC.BUTTONS.CANCEL"
                        onClick={close}
                        disabled={saving}
                        style={{ marginRight: 10 }}
                    />
                    <Button
                        text={success ? 'STATIC.BUTTONS.CLOSE' : 'STATIC.BUTTONS.SAVE'}
                        type="primary"
                        onClick={onPrimaryClick}
                        showSpinner={saving}
                        disabled={primaryDisabled}
                    />
                </div>
            </>
        </Dialog>
    );
}

export default ChangePasswordDialog;
